import { useQuery } from '@tanstack/react-query';

import {
  getCentersFromRequirements,
  getClientRequirementsFromCenter,
  getClientRequirementsListFromCenter,
  getMedicalCenter,
  getMedicalCenters,
  getMedicalCenterFund,
  getRequirementsFromCenter,
  getManagedMedicalCenters,
  getCanAddRequirementsToCase,
  getMedicalCenterLogo,
  getMedicalCenterFooter,
  getPageDesign,
} from '@/modules/api/medicalCenters';
import { DAYS } from '@core/constants';

/**
 * Hook to get all medical centers
 */
export function useMedicalCenters({
  clientID = null,
  subAdministrator = undefined,
  enabled = true,
  isSubAdministrator = null,
  managerCenters = null,
}) {
  return useQuery({
    queryKey: [
      'medicalCenters',
      { clientID, subAdministrator, isSubAdministrator, managerCenters },
    ],
    queryFn: () =>
      getMedicalCenters({ clientID, subAdministrator, isSubAdministrator, managerCenters }),
    enabled: enabled,
    // avoid refetching on every mount/navigation while session is active
    staleTime: 1000 * 60 * 30, // 30 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * Hook to get all medical centers fund
 */
export function useMedicalCenterFund({
  clientID,
  state,
  forBalance = false,
  fundType,
  enabled = undefined,
  select,
  isActive = undefined,
}) {
  return useQuery({
    queryKey: ['medicalCenterFund', clientID, state, { forBalance, fundType, isActive }],
    queryFn: () => getMedicalCenterFund({ clientID, state, forBalance, fundType, isActive }),
    enabled: enabled === undefined ? !!clientID : enabled,
    select,
  });
}

/**
 * Hook to get a single medical center
 * @param {number} centerId
 */
export function useSingleMedicalCenter(centerId, options = {}) {
  return useQuery({
    queryKey: ['medicalCenter', centerId],
    queryFn: () => getMedicalCenter(centerId),
    staleTime: Infinity,
    ...options,
  });
}

export function useGetMedicalCenterLogo({ centerId = null, studyId = null } = {}) {
  return useQuery({
    queryKey: ['medicalCenterLogo', centerId, studyId],
    queryFn: () => getMedicalCenterLogo({ centerId, studyId }),
    enabled: !!(centerId || studyId),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
}

// --- FOOTER ---
export function useGetMedicalCenterFooter({ centerId = null, studyId = null } = {}) {
  return useQuery({
    queryKey: ['medicalCenterFooter', centerId, studyId],
    queryFn: () => getMedicalCenterFooter({ centerId, studyId }),
    enabled: !!(centerId || studyId),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
}

// --- PAGE DESIGN ---
export function useGetPageDesign({ centerId = null, studyId = null } = {}) {
  return useQuery({
    queryKey: ['pageDesign', centerId, studyId],
    queryFn: () => getPageDesign({ centerId, studyId }),
    enabled: !!(centerId || studyId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get list of medical centers from a list of requirements
 * @param {object} params
 * @param {number[]?} params.requirements Requirement being requested
 * @param {number?} params.client ID of the client that the patient is associated to
 * @param {number?} params.subAdministrator ID of the sub-administrator
 * @param {number?} params.state State of the medical center
 * @param {boolean} params.enabled If query should be enabled
 * @param {import("@tanstack/react-query").UseQueryOptions} params.options
 */
export function useCentersFromRequirements({
  requirements,
  client = null,
  subAdministrator = null,
  state = null,
  enabled,
  options = {},
}) {
  return useQuery({
    queryKey: ['medicalCenters', 'requirements', { requirements, subAdministrator, client, state }],
    queryFn: () =>
      getCentersFromRequirements({
        requirements,
        client,
        state,
        subAdministrator,
      }),
    enabled,
    ...options,
  });
}

/**
 * Hook to get requirements of a type offered from a medical center to a client
 * @param {object} params
 * @param {number?} params.centerId
 * @param {number?} params.client
 * @param {number?} params.requirementType
 * @param {number?} params.subAdministrator
 * @param {import("@tanstack/react-query").UseQueryOptions?} params.options
 */
export function useMedicalCenterClientRequirements({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator,
  options = {},
}) {
  return useQuery({
    queryKey: [
      'medicalCenter',
      'requirements',
      Number(centerId),
      { client, requirementType, state, subAdministrator },
    ],
    queryFn: () =>
      getClientRequirementsFromCenter({
        centerId: Number(centerId),
        client: Number(client),
        requirementType: Number(requirementType),
        state: state,
        subAdministrator,
      }),
    enabled: centerId !== null && (client !== null || subAdministrator !== null),
    ...options,
  });
}

/**
 * Hook to get requirements of a type offered from a medical center to a client
 * @param {object} params
 * @param {number?} params.centerId
 * @param {number?} params.client
 * @param {number?} params.requirementType
 * @param {number?} params.state
 * @param {number?} params.subAdministrator
 */
export function useMedicalCenterClientRequirementsList({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator = null,
}) {
  return useQuery({
    queryKey: [
      'medicalCenterList',
      'requirements',
      Number(centerId),
      { client, requirementType, state, subAdministrator },
    ],
    queryFn: () =>
      getClientRequirementsListFromCenter({
        centerId: Number(centerId),
        client: Number(client),
        requirementType: Number(requirementType),
        state: state,
        subAdministrator,
      }),
    enabled: centerId !== null && (client !== null || subAdministrator !== null),
  });
}

/**
 * `useQuery` hook to get managed medical centers
 * @author gabrielm
 */
export function useManagedMedicalCenters({ subAdministrator = null } = {}) {
  return useQuery({
    queryKey: ['managedMedicalCenters', { subAdministrator }],
    queryFn: () => getManagedMedicalCenters(subAdministrator),
  });
}

/**
 * Hook to check if a medical center can add requirements to a case
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useCanAddRequirementsToCase(medicalCenter, options = {}) {
  return useQuery({
    queryKey: ['canAddRequirementsToCase', medicalCenter],
    queryFn: () => getCanAddRequirementsToCase(medicalCenter),
    ...options,
  });
}

/**
 * @typedef MedicalCenterRequirement
 * @property {number} id
 * @property {object} client
 * @property {number} client.id
 * @property {string} client.name
 * @property {string} company_client_name
 * @property {number} requirement
 * @property {string} requirement_name
 * @property {string} type_requirement_name
 * @property {number | string} price_ref
 * @property {object[]} days
 */

/**
 * Hook to get requirements associated to a medical center
 * @param {number} centerId
 */
export function useMedicalCentersRequirements(centerId) {
  return useQuery({
    queryKey: ['medicalCenter', 'requirements', Number(centerId)],
    queryFn: () => getRequirementsFromCenter(centerId),
    select: data =>
      data.map(req => {
        /**@type {MedicalCenterRequirement} */
        const formattedData = {
          id: req.id,
          client: { id: req.company_client, name: req.company_client_name },
          company_client_name: req.company_client_name,
          requirement: req.requirement,
          requirement_name: req.requirement_name,
          type_requirement_name: req.type_requirement_name,
          price_ref: req.requirement_price_ref,
          days: req.requirement_day.map(reqDay => ({
            ...DAYS.find(day => day.apiLabel === reqDay.day),
            slots: reqDay.quota,
          })),
        };
        return formattedData;
      }),
  });
}
